import React, { useRef, useState } from 'react';

import { FileUp } from 'lucide-react';

import Button from '../../common/Button';
import { useGetDatasetById } from '../../hooks/tanstack/useDatasetsHook';
import { useGetIFS } from '../../hooks/tanstack/useIFSHook';
import {
  useGetRouteParams,
  useHandleImportIFS,
} from '../../hooks/tanstack/useUtilsHook';
import { IFSTable } from '../IfsTable';
import LoadingDialog from '../LoadingDialog';
import SuccessDialog from '../SuccessDialog';

import AlertDialog from './AlertDialog';
import DownloadIFSTemplatesDialog from './DownloadIFSTemplatesDialog';

export const ErrorContent = (data: string[] | string) => (
  <div className="flex flex-col gap-2">
    <p>Invalid file(s):</p>
    <ul className="list-disc px-8">
      {Array.isArray(data) ? (
        data.map((error: string, index: number) => <li key={index}>{error}</li>)
      ) : (
        <li>{data}</li>
      )}
    </ul>
  </div>
);

const IFSTableSection = () => {
  const inputFileRef = useRef<HTMLInputElement | null>(null);

  const [openErrorDialog, setOpenErrorDialog] = useState(false);
  const [openSuccessDialog, setOpenSuccessDialog] = useState(false);

  const { projectId, orgId, datasetId } = useGetRouteParams();

  const { importIfs, isPending, error } = useHandleImportIFS({
    inputFileRef,
    handleError: () => {
      setOpenErrorDialog(true);
    },
  });

  const { data: dataset } = useGetDatasetById(
    {
      orgId,
      datasetId,
      projectId,
    },
    {
      enabled: !!orgId || !!datasetId || !!projectId,
    }
  );

  const { data: ifs } = useGetIFS(
    {
      projectId,
      datasetId,
      orgId,
    },
    {
      enabled: !!datasetId && !!orgId && !!projectId,
    }
  );

  return (
    <div className="space-y-3">
      <div className="flex justify-between">
        <div className="flex items-center">
          <p className="text-xl font-bold">Upload files</p>
        </div>
        <div className="flex gap-3">
          {dataset && ifs && (
            <DownloadIFSTemplatesDialog dataset={dataset} ifs={ifs} />
          )}
          <Button
            variant="contained_blue"
            className="relative flex items-center text-white"
          >
            <input
              ref={inputFileRef}
              type="file"
              accept=".xlsx"
              id="import_ifs"
              onChange={importIfs}
              multiple
              className="absolute w-[140px] cursor-pointer opacity-0"
            />
            <FileUp size={20} />
            Upload File(s)
          </Button>
        </div>
      </div>
      <IFSTable />
      <div className="h-4" />
      <AlertDialog
        title="Error Found: Please Review"
        open={openErrorDialog}
        content={error?.content}
        handleOnClose={() => {
          setOpenErrorDialog(false);
        }}
      />
      <SuccessDialog
        content="Input File Set imported successfully."
        open={openSuccessDialog}
        handleOnClose={() => {
          setOpenSuccessDialog(false);
        }}
      />
      <LoadingDialog title="Uploading" open={isPending} />
    </div>
  );
};

export default IFSTableSection;
